import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Fade, IconButton } from "@mui/material";
import * as React from "react";
import { Ramp } from "../../../../../../types";
import { WizardStepper } from "./components/WizardStepper";
import { CalibrationStep } from "./steps/CalibrationStep";
import { ConfirmStep } from "./steps/ConfirmStep";
import { useSetupWizardStore } from "./useSetupWizardStore";

type Props = {
  ramp: Ramp;
  setRamp: (updater: (prev: Ramp) => Ramp) => void;
  onComplete: () => void;
  onCancel: () => void;
  /** Persist ramp to database (auto-save on wizard complete) */
  onSave: (ramp: Ramp) => void;
};

export const SetupWizard: React.FC<Props> = ({
  ramp,
  setRamp,
  onComplete,
  onCancel,
  onSave,
}) => {
  const { currentStep, exitWizard } = useSetupWizardStore();

  const handleCancel = () => {
    exitWizard();
    onCancel();
  };

  const renderStep = () => {
    switch (currentStep) {
      case "calibrate":
        return <CalibrationStep ramp={ramp} setRamp={setRamp} />;
      case "confirm":
        return (
          <ConfirmStep ramp={ramp} setRamp={setRamp} onComplete={onComplete} onSave={onSave} />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 100,
        pointerEvents: "none",
      }}
    >
      {/* Minimal floating header - back button + dots */}
      <Fade in timeout={300}>
        <Box
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            right: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pointerEvents: "auto",
          }}
        >
          <IconButton
            onClick={handleCancel}
            sx={{
              backgroundColor: "background.paper",
              boxShadow: 2,
              "&:hover": {
                backgroundColor: "background.paper",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box
            sx={{
              backgroundColor: "background.paper",
              borderRadius: 3,
              px: 2,
              py: 1,
              boxShadow: 2,
            }}
          >
            <WizardStepper />
          </Box>
        </Box>
      </Fade>

      {/* Step content - rendered as floating overlay */}
      <Fade in timeout={400} key={currentStep}>
        <Box>{renderStep()}</Box>
      </Fade>
    </Box>
  );
};
